/**
 * The three kinds of record the S4CA registry holds, shared between the Worker and the
 * browser.
 *
 * One list rather than two, because the two copies would have to agree about the exact
 * spelling of three strings — and a `fund_type` the browser wrote as 'training_school'
 * would be refused by the CHECK on the column with nothing on screen to say why. The
 * column, the import's `fundTypeCell`, the API's filter and the three Register pages all
 * read this file.
 *
 * `label` is the singular as a person says it. There is deliberately no plural here: the
 * Register pages title themselves (see KINDS in PartnerList) and the rest of the app
 * shows this against one record at a time.
 */
export const FUND_TYPES = ['staff', 'project', 'training']

export const FUND_TYPE_LABELS = {
  staff: 'Partner Unit',
  project: 'Project',
  training: 'Training School',
}

/**
 * What to call a record of this kind, falling back to the staff wording.
 *
 * The fallback is the point: `fund_type` is NOT NULL in the database, but a row read
 * through an older cached response — or one of the several views that do not carry the
 * column — arrives here as undefined, and a card that says "Partner Unit" is right far
 * more often than one that says "undefined".
 */
export const fundTypeLabel = (v) => FUND_TYPE_LABELS[v] ?? FUND_TYPE_LABELS.staff

/**
 * The kinds an update's "Also about" link may name, given the kind of record that wrote it.
 *
 * A partner unit's update may be about the training school it teaches at or the project it
 * runs — never another partner unit, which would be a second author rather than a subject.
 * An update written by a school or a project may name any other record, the partner unit
 * included: that is the school writing about the unit that teaches there, and the unit's
 * own page should show it the way the school's page shows the unit's.
 *
 * Frozen, and the same array every time for the same kind, so a picker can take it as a
 * prop without being handed a fresh dependency on every render.
 */
const SECONDARY_KINDS = {
  staff: Object.freeze(['training', 'project']),
  other: Object.freeze(['staff', 'training', 'project']),
}
export const secondaryKindsFor = (authorKind) =>
  (authorKind ?? 'staff') === 'staff' ? SECONDARY_KINDS.staff : SECONDARY_KINDS.other

/**
 * How many current records of one kind are in a lookups payload.
 *
 * This is the sidebar badge's fallback — what it shows before the Register page it
 * belongs to has been opened and reported its own filtered total. It exists as a function
 * here, rather than as a filter written inline three times, because getting it wrong is
 * invisible until somebody notices the badge changing when they click it: the number has
 * to be exactly what the page behind it shows, and the page shows one kind with alumni
 * left out.
 *
 * Two details that were each a bug:
 *
 *   - **alumni, not inactive.** Every list page leaves out `alumni` and keeps everything
 *     else. Counting `status === 'active'` instead silently drops any record marked
 *     inactive, which is a state the archive has and the pages do not hide.
 *   - **the kind.** Since the registry holds three kinds, a count of all of them is the
 *     Partner Units badge reading the whole registry.
 *
 * `?? 'staff'` covers a row from a lookups payload cached before the column existed —
 * staff is what every record in this registry was until then.
 *
 * A third detail, and the same bug a third time: **the partner units page opens narrower
 * than the other two.** It shows the active units the registry found staff in, so its
 * badge counts that and not "everything that is not alumni". The other two kinds have no
 * such filter and keep the plain rule.
 */
const onItsPage = (p, kind) => {
  if (kind !== 'staff') return p.status !== 'alumni'
  if (p.status !== 'active') return false
  /* Absent, not empty. A payload cached before `staff_partners` was sent carries no such
     key, and reading that as "no staff" would empty the badge for every unit in the
     archive; a unit the file has counted and found no staff in carries the key with null
     or 0, and is one of the ones the page leaves out. */
  return 'staff_partners' in p ? (p.staff_partners ?? 0) > 0 : true
}

export const countOfKind = (partners, kind) =>
  Array.isArray(partners)
    ? partners.filter((p) => (p.fund_type ?? 'staff') === kind && onItsPage(p, kind)).length
    : undefined
